import { z } from 'zod';

import {
  authenticatedAtom,
  codWebAtom,
  leadIntegrationEnabledAtom,
  outboundCallJournalEnabledAtom,
  store,
} from '@/lib/store';
import { recordOutgoingCrm } from '@/utils/outgoing-call-journal';
import {
  callApexForLeadPhoneBook,
  callApexForUserInfo,
  callCorporateUserInfo,
} from '@/utils/salesforce-apex';
import { ensureSalesforceOpenCtiReady } from '@/utils/salesforce-opencti';

import type { IProvider, LeadPhoneBook, UserInfo } from './provider-types';

const AuthInfoSchema = z.object({
  secret: z.string().min(1),
  codWeb: z.string().min(1),
});

let salesforceSecret = '';
let salesforceCodWeb = '';

const withOpenCti = (callback: () => void) => {
  void ensureSalesforceOpenCtiReady()
    .then(callback)
    .catch((error) => {
      console.error('[Salesforce Open CTI] operation failed', error);
    });
};

export const salesforceProvider: IProvider = {
  id: 'salesforce',

  initialize: () => ensureSalesforceOpenCtiReady(),

  async authenticate(): Promise<void> {
    await ensureSalesforceOpenCtiReady();

    return new Promise<void>((resolve, reject) => {
      try {
        sforce.opencti.getCallCenterSettings({
          callback: (response) => {
            if (!response.success) {
              store.set(authenticatedAtom, false);
              reject(
                new Error(`getCallCenterSettings failed: ${response.errors}`)
              );
              return;
            }
            try {
              const raw = response.returnValue as Record<string, unknown>;
              const parsed = AuthInfoSchema.parse({
                secret: raw['/authInfo/secret'],
                codWeb: raw['/authInfo/codWeb'],
              });
              salesforceSecret = parsed.secret;
              salesforceCodWeb = parsed.codWeb;
              store.set(codWebAtom, salesforceCodWeb);
              store.set(authenticatedAtom, true);
              resolve();
            } catch (error) {
              store.set(authenticatedAtom, false);
              reject(error as Error);
            }
          },
        });
      } catch (error) {
        store.set(authenticatedAtom, false);
        reject(error as Error);
      }
    });
  },

  async getAuthHeaders() {
    const headers: Record<string, string> = {};
    if (salesforceSecret) headers['X-API-Secret'] = salesforceSecret;
    return headers;
  },

  openCase: (caseId) => {
    withOpenCti(() => {
      sforce.opencti.screenPop({
        type: sforce.opencti.SCREENPOP_TYPE.SOBJECT,
        params: { recordId: caseId },
        callback: (response) => {
          if (!response.success) {
            console.warn(
              `Something went wrong while screenPopToCase ${caseId}`,
              response.errors
            );
          }
        },
      });
    });
  },

  openLead: (leadId, created) => {
    withOpenCti(() => {
      const handleScreenPop = (recordId: string) => {
        sforce.opencti.screenPop({
          type: sforce.opencti.SCREENPOP_TYPE.SOBJECT,
          params: { recordId },
          callback: (response) => {
            if (!response.success) {
              console.warn(
                `Something went wrong while screenPopToLead ${recordId}`,
                response.errors
              );
            }
          },
        });
      };

      if (created) {
        sforce.opencti.getAppViewInfo({
          callback: (response) => {
            if (response.success) {
              const url = response.returnValue.url.split('.com')[0];

              if (url) {
                sforce.opencti.screenPop({
                  type: sforce.opencti.SCREENPOP_TYPE.URL,
                  params: {
                    url: `${url}.com/lightning/r/Lead/${leadId}/edit?count=1&backgroundContext=%2Flightning%2Fr%2FLead%2F${leadId}%2Fview`,
                  },
                });
              } else {
                handleScreenPop(leadId);
              }
            } else {
              console.error(
                'Erro ao tentar pegar URL do cliente (getAppViewInfo) Errors:',
                response.errors
              );
              handleScreenPop(leadId);
            }
          },
        });
      } else {
        handleScreenPop(leadId);
      }
    });
  },

  openWidget: () => {
    withOpenCti(() => {
      sforce.opencti.setSoftphonePanelVisibility({ visible: true });
    });
  },

  enableClickToDial: (listener: (number: string) => void) => {
    withOpenCti(() => {
      sforce.opencti.onClickToDial({
        listener: async ({ number }) => listener(String(number)),
      });
      sforce.opencti.enableClickToDial();
    });
  },

  async logCall({ contactId }: { contactId: string }) {
    await ensureSalesforceOpenCtiReady();

    await recordOutgoingCrm(contactId, {
      leadIntegration: store.get(leadIntegrationEnabledAtom),
      outboundCallJournal: store.get(outboundCallJournalEnabledAtom),
    });
  },

  async getUserInfo(): Promise<UserInfo> {
    try {
      return await callCorporateUserInfo();
    } catch {
      /* Legacy package compatibility. */
    }
    try {
      return await callApexForUserInfo('UserInfoService');
    } catch (primaryError) {
      console.warn(
        'UserInfoService failed, attempting HitphoneUserInfoService as a fallback',
        primaryError
      );
      return await callApexForUserInfo('HitphoneUserInfoService');
    }
  },

  async getLeadPhoneBook(): Promise<LeadPhoneBook> {
    return await callApexForLeadPhoneBook();
  },
};
